import React, { useEffect, useState } from 'react'
import {
  Select,
  Input,
  DatePicker,
  Button,
} from '@surecompanies/core_components'
import { Badge } from 'antd'
import { FilterOutlined, SyncOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import {
  getPaymentAssignmentStatusLabel,
  PaymentAssignmentStatusId,
  PaymentFilters,
} from '../Utils/types'
import { ExportPaymentAssignmentButton } from '../Utils/ExportPaymentAssignment'
import { TopBarContainer, LeftSection, RightSection } from './styles'
import type { FilterUpdateFn, SortState } from './types'

const { Search } = Input

interface PaymentAssignmentFilterTopBarProps {
  currentDate: Dayjs
  handleDateChange: (date: Dayjs | null) => void
  statusOptions: PaymentAssignmentStatusId[]
  statusesValue: string[]
  updateFilter: FilterUpdateFn
  onSearch: () => void
  isSearching: boolean
  advancedFilterCount: number
  setDrawerOpen: (open: boolean) => void
  isOnlyChecker: boolean
  filters: PaymentFilters
  sort?: SortState | null
}

export default function PaymentAssignmentFilterTopBar({
  currentDate,
  handleDateChange,
  statusOptions,
  statusesValue,
  updateFilter,
  onSearch,
  isSearching,
  advancedFilterCount,
  setDrawerOpen,
  isOnlyChecker,
  filters,
  sort,
}: PaymentAssignmentFilterTopBarProps) {
  const [refreshKey, setRefreshKey] = useState(false)

  useEffect(() => {
    if (refreshKey) {
      onSearch()
      setRefreshKey(false)
    }
  }, [refreshKey])

  return (
    <TopBarContainer>
      <LeftSection>
        <DatePicker
          picker="month"
          value={currentDate}
          onChange={(date) => {
            handleDateChange(date)
          }}
          style={{ minWidth: 160 }}
          format="MMMM YYYY"
          size="large"
          disabled={isSearching}
        />

        <Select
          mode="multiple"
          placeholder="All Statuses"
          value={statusesValue}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option: any) => {
            const label = option?.children
            return typeof label === 'string'
              ? label.toLowerCase().includes(input.toLowerCase())
              : false
          }}
          onChange={(value) => {
            if (Array.isArray(value)) {
              updateFilter('statuses', value)
            }
          }}
          onDropdownVisibleChange={(open) => {
            if (!open) onSearch()
          }}
          style={{ minWidth: 200 }}
          size="large"
          maxTagCount="responsive"
          disabled={isSearching}
          allowClear
          onClear={() => {
            updateFilter('statuses', [])
            setRefreshKey(true)
          }}
        >
          {statusOptions.map((status) => (
            <Select.Option key={status} value={status}>
              {getPaymentAssignmentStatusLabel(status)}
            </Select.Option>
          ))}
        </Select>

        <Search
          placeholder="Search by benefit ID, Company EE ID, employee name, or insured person name"
          value={filters.search ?? ''}
          onChange={(e) => updateFilter('search', e.target.value || undefined)}
          onSearch={() => {
            onSearch()
          }}
          onPressEnter={() => {
            onSearch()
          }}
          allowClear
          onClear={() => {
            updateFilter('search', undefined)
            setRefreshKey(true)
          }}
          size="large"
          loading={isSearching}
          disabled={isSearching}
          style={{ flex: 1 }}
        />
      </LeftSection>

      <RightSection>
        <Button
          icon={<SyncOutlined spin={isSearching} />}
          onClick={onSearch}
          disabled={isSearching}
          size="large"
        >
          Refresh
        </Button>

        <Badge count={advancedFilterCount} offset={[-5, 5]}>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setDrawerOpen(true)}
            size="large"
          >
            Filters
          </Button>
        </Badge>

        {isOnlyChecker && (
          <ExportPaymentAssignmentButton
            filters={filters}
            sort={sort}
            disabled={isSearching}
          />
        )}
      </RightSection>
    </TopBarContainer>
  )
}
